import Vue from "vue";
import Vuex from "vuex";
import i18n, { locales } from "@/i18n";
import importData from "@/utils/import-data";
import setupCrossfilter from "@/utils/setup-crossfilter";
import { MAX_PICKED_THEMES } from "@/utils/constants";
import {
  getAllThemesSorted,
  getAllTargetsOfPickedThemes,
  getSankeyData,
  toggleArrayItem,
  getHighlightedFromThemes,
  getHighlightedFromMoods,
  getHighlightedFromMotifs,
  getHighlightedFromThemeMoodFlows,
  getHighlightedFromThemeMotifFlows,
  getHighlightedFromPoem
} from "@/utils/process-data";
import { scaleOrdinal } from "d3-scale";
import { ascending } from "d3-array";

export const INTERACTION_CATEGORIES = {
  WORDCLOUD_THEME: "WORDCLOUD_THEME",
  WORDCLOUD_POEM: "WORDCLOUD_POEM",
  SANKEY_THEME: "SANKEY_THEME",
  SANKEY_MOOD: "SANKEY_MOOD",
  SANKEY_THEME_MOOD: "SANKEY_THEME_MOOD",
  SANKEY_MOTIF: "SANKEY_MOTIF",
  SANKEY_THEME_MOTIF: "SANKEY_THEME_MOTIF"
};
export const INTERACTION_ACTIONS = {
  HOVER_ADD: "HOVER_ADD",
  HOVER_REMOVE: "HOVER_REMOVE",
  CLICK_TOGGLE: "CLICK_TOGGLE"
};
const arrayAction = {
  HOVER_ADD: (array, item) => [item],
  HOVER_REMOVE: () => [],
  CLICK_TOGGLE: toggleArrayItem
};

Vue.use(Vuex);

let cf, poemsDimension, poemsGroup;

export default new Vuex.Store({
  state: {
    locale: "en",
    poems: [],
    allThemes: [],
    highlightedThemes: [],
    highlightedMotifs: [],
    highlightedMoods: [],
    highlightedPoems: [],
    highlightedThemeToMoodFlows: [],
    highlightedThemeToMotifFlows: [],
    highlightFreezed: false,
    interactingCategory: null,
    interactingCategoryValues: [],
    interactingAction: null,
    pickedThemes: [],
    allMoodsOfPickedThemes: [],
    allMotifsOfPickedThemes: [],
    pickedPoem: null,
    sankeyThemeToMoodFlows: [],
    sankeyThemeToMotifFlows: [],
    sankeyMoodsSet: new Set(),
    sankeyMotifsSet: new Set(),
    sankeyPoems: [],
    pickedMoods: [],
    pickedMotifs: [],
    moodThreshold: 4,
    motifThreshold: 12,
    allYears: [],
    allSentiments: [],
    allYearsSentiments: [],
    poemsGroupYearSentiment: [],
    filteredPoems: [],
    isFiltered: false,
    isShowingInstructionIcons: true, //false
    appError: null
  },
  mutations: {
    updateLocale(state, locale) {
      state.locale = locale;
    },
    updatePoems(state, poems) {
      state.poems = poems;
    },
    updateAllYears(state, years) {
      state.allYears = years;
    },
    updateAllSentiments(state, sentiments) {
      state.allSentiments = sentiments;
    },
    updateAllYearsSentiments(state, yearsSentiments) {
      state.allYearsSentiments = yearsSentiments;
    },
    updateHighlightedThemes(state, themes) {
      state.highlightedThemes = themes;
    },
    updateHighlightedMotifs(state, motifs) {
      state.highlightedMotifs = motifs;
    },
    updateHighlightedMoods(state, moods) {
      state.highlightedMoods = moods;
    },
    updateHighlightedPoems(state, poems) {
      state.highlightedPoems = poems;
    },
    updateHighlightedThemeToMoodFlows(state, flows) {
      state.highlightedThemeToMoodFlows = flows;
    },
    updateHighlightedThemeToMotifFlows(state, flows) {
      state.highlightedThemeToMotifFlows = flows;
    },
    updateHighlightFreezed(state, highlightFreezed) {
      state.highlightFreezed = highlightFreezed;
    },
    updateInteractingCategory(state, chart) {
      state.interactingCategory = chart;
    },
    updateInteractingCategoryValues(state, values) {
      state.interactingCategoryValues = values;
    },
    updateInteractingAction(state, action) {
      state.interactingAction = action;
    },
    updatePickedThemes(state, themes) {
      state.pickedThemes = themes;
    },
    updateAllMoodsOfPickedThemes(state, moods) {
      state.allMoodsOfPickedThemes = moods;
    },
    updateAllMotifsOfPickedThemes(state, motifs) {
      state.allMotifsOfPickedThemes = motifs;
    },
    updatePickedPoem(state, poem) {
      state.pickedPoem = poem;
    },
    updateSankeyThemeToMoodFlows(state, flows) {
      state.sankeyThemeToMoodFlows = flows;
    },
    updateSankeyThemeToMotifFlows(state, flows) {
      state.sankeyThemeToMotifFlows = flows;
    },
    updateSankeyMoodsSet(state, set) {
      state.sankeyMoodsSet = set;
    },
    updateSankeyMotifsSet(state, set) {
      state.sankeyMotifsSet = set;
    },
    updateAllThemes(state, themes) {
      state.allThemes = themes;
    },
    updateSankeyPoems(state, poems) {
      state.sankeyPoems = poems;
    },
    updateFilteredPoems(state, poems) {
      state.filteredPoems = poems;
    },
    updateIsFiltered(state, isFiltered) {
      state.isFiltered = isFiltered;
    },
    updateIsShowingInstructionIcons(state, isShowing) {
      state.isShowingInstructionIcons = isShowing;
    },
    updatePickedMoods(state, moods) {
      state.pickedMoods = moods;
    },
    updatePickedMotifs(state, motifs) {
      state.pickedMotifs = motifs;
    },
    updateMotifThreshold(state, threshold) {
      state.motifThreshold = threshold;
    },
    updateMoodThreshold(state, threshold) {
      state.moodThreshold = threshold;
    },
    updatePoemsGroupYearSentiment(state, poemsGroupYearSentiment) {
      state.poemsGroupYearSentiment = poemsGroupYearSentiment;
    },
    updateAppError(state, error) {
      state.appError = error;
    }
  },
  actions: {
    async updateLocale({ commit, dispatch, getters }, locale) {
      const memoize = async localeObj => {
        if (!localeObj.data) {
          localeObj.data = await importData(localeObj.dataUrl);
        }
        return localeObj.data;
      };

      try {
        const localeObj = locales.find(d => d.value === locale);

        let poems = await memoize(localeObj);
        commit("updatePoems", poems);

        commit("updateLocale", locale);

        [cf, poemsDimension, poemsGroup] = setupCrossfilter(poems);

        const allYears = [...new Set(poems.map(d => d.year))].sort();
        commit("updateAllYears", allYears);

        const allYearsSentiments = poemsGroup.yearSentiment
          .all()
          .map(d => d.key);
        commit("updateAllYearsSentiments", allYearsSentiments);

        const poemsGroupYearSentiment = Object.freeze(
          poemsGroup.yearSentiment.all()
        );
        commit("updatePoemsGroupYearSentiment", poemsGroupYearSentiment);

        const sentiments = localeObj.sentiments;
        commit("updateAllSentiments", sentiments);

        commit("updatePickedThemes", getters.defaultPickedThemes);

        dispatch("updateFiltered");
      } catch (error) {
        console.error(error);
        commit("updateAppError", i18n.t("error.dataRetrieval"));
      }
    },
    /**
     * SANKEY
     * updateHighlightedTheme
     * updateHighlightedMood
     * updateHighlightedMoodsWithTheme
     * updateHighlightedMotif
     * updateHighlightedMotifsWithTheme
     * - these filter the highlightedPoems from the sankeyPoems
     *
     * WORDCLOUD
     * updatePickedTheme -> theme, motifs, moods
     * updatePickedPoem -> motifs, moods
     * updateHoveredPoem
     * - these updates the sankey
     * updateHighlightedTheme
     * - this filters the highlightedPoems from the sankeyPoems
     *
     * TIMELINE
     * updateHighlightedYearsSentiments
     * - this updates the filteredPoems, and filteredPoems is used to derive sankeyPoems
     *
     * POEM TABLE
     */
    updateHighlightedThemes(
      { state, commit, dispatch },
      { value: theme, action }
    ) {
      const themes = arrayAction[action](state.highlightedThemes, theme);
      if (themes.length === 0) {
        dispatch("resetHighlight");
      } else {
        let {
          moods,
          motifs,
          toMoodFlows,
          toMotifFlows,
          poems
        } = getHighlightedFromThemes(
          state.sankeyThemeToMoodFlows,
          state.sankeyThemeToMotifFlows,
          themes
        );
        if (
          state.highlightedPoems.length > 0 &&
          state.interactingCategory === INTERACTION_CATEGORIES.WORDCLOUD
        ) {
          // This happens when one poem is highlighted through the word cloud
          state.highlightedPoems.forEach(poem => {
            toMoodFlows = toMoodFlows.filter(f => f.poems.has(poem));
            toMotifFlows = toMotifFlows.filter(f => f.poems.has(poem));
            motifs = motifs.filter(motif => poem.motifs.includes(motif));
            moods = moods.filter(mood => poem.moods.includes(mood));
            poems = state.highlightedPoems;
          });
        }
        commit("updateHighlightedThemes", themes);
        commit("updateHighlightedMoods", moods);
        commit("updateHighlightedMotifs", motifs);
        commit("updateHighlightedThemeToMoodFlows", toMoodFlows);
        commit("updateHighlightedThemeToMotifFlows", toMotifFlows);
        commit("updateHighlightedPoems", poems);
      }
    },
    updateHighlightedMoods(
      { state, commit, dispatch },
      { value: mood, action }
    ) {
      const moods = arrayAction[action](state.highlightedMoods, mood);
      if (moods.length === 0) {
        dispatch("resetHighlight");
      } else {
        const {
          themes,
          motifs,
          toMoodFlows,
          toMotifFlows,
          poems
        } = getHighlightedFromMoods(
          state.sankeyThemeToMoodFlows,
          state.sankeyThemeToMotifFlows,
          moods
        );
        commit("updateHighlightedThemes", themes);
        commit("updateHighlightedMoods", moods);
        commit("updateHighlightedMotifs", motifs);
        commit("updateHighlightedThemeToMoodFlows", toMoodFlows);
        commit("updateHighlightedThemeToMotifFlows", toMotifFlows);
        commit("updateHighlightedPoems", poems);
      }
    },
    updateHighlightedMoodsWithTheme(
      { state, commit, dispatch },
      { value: [theme, mood], action }
    ) {
      const toMoodFlow = state.sankeyThemeToMoodFlows.find(
        f => f.source === theme && f.target === mood
      );
      const toMoodFlows = arrayAction[action](
        state.highlightedAuthorToMoodFlows,
        toMoodFlow
      );
      if (toMoodFlows.length === 0) {
        dispatch("resetHighlight");
      } else {
        const {
          themes,
          moods,
          motifs,
          toMotifFlows,
          poems
        } = getHighlightedFromThemeMoodFlows(
          state.sankeyThemeToMotifFlows,
          toMoodFlows
        );
        commit("updateHighlightedThemes", themes);
        commit("updateHighlightedMoods", moods);
        commit("updateHighlightedMotifs", motifs);
        commit("updateHighlightedThemeToMoodFlows", toMoodFlows);
        commit("updateHighlightedThemeToMotifFlows", toMotifFlows);
        commit("updateHighlightedPoems", poems);
      }
    },
    updateHighlightedMotifs(
      { state, commit, dispatch },
      { value: motif, action }
    ) {
      const motifs = arrayAction[action](state.highlightedMotifs, motif);
      if (motifs.length === 0) {
        dispatch("resetHighlight");
      } else {
        const {
          themes,
          moods,
          toMoodFlows,
          toMotifFlows,
          poems
        } = getHighlightedFromMotifs(
          state.sankeyThemeToMoodFlows,
          state.sankeyThemeToMotifFlows,
          motifs
        );
        commit("updateHighlightedThemes", themes);
        commit("updateHighlightedMoods", moods);
        commit("updateHighlightedMotifs", motifs);
        commit("updateHighlightedThemeToMoodFlows", toMoodFlows);
        commit("updateHighlightedThemeToMotifFlows", toMotifFlows);
        commit("updateHighlightedPoems", poems);
      }
    },
    updateHighlightedMotifsWithTheme(
      { state, commit, dispatch },
      { value: [theme, motif], action }
    ) {
      const toMotifFlow = state.sankeyThemeToMotifFlows.find(
        f => f.source === theme && f.target === motif
      );
      const toMotifFlows = arrayAction[action](
        state.highlightedThemeToMotifFlows,
        toMotifFlow
      );
      if (toMotifFlows.length === 0) {
        dispatch("resetHighlight");
      } else {
        const {
          themes,
          moods,
          motifs,
          toMoodFlows,
          poems
        } = getHighlightedFromThemeMotifFlows(
          state.sankeyThemeToMoodFlows,
          toMotifFlows
        );
        commit("updateHighlightedThemes", themes);
        commit("updateHighlightedMoods", moods);
        commit("updateHighlightedMotifs", motifs);
        commit("updateHighlightedThemeToMoodFlows", toMoodFlows);
        commit("updateHighlightedThemeToMotifFlows", toMotifFlows);
        commit("updateHighlightedPoems", poems);
      }
    },
    updateHighlightedByPoem({ state, commit, dispatch }, { value: poem }) {
      if (!poem) {
        dispatch("resetHighlight");
      } else {
        const {
          themes,
          moods,
          motifs,
          toMoodFlows,
          toMotifFlows,
          poems
        } = getHighlightedFromPoem(
          state.sankeyThemeToMoodFlows,
          state.sankeyThemeToMotifFlows,
          poem
        );
        commit("updateHighlightedThemes", themes);
        commit("updateHighlightedMoods", moods);
        commit("updateHighlightedMotifs", motifs);
        commit("updateHighlightedThemeToMoodFlows", toMoodFlows);
        commit("updateHighlightedThemeToMotifFlows", toMotifFlows);
        commit("updateHighlightedPoems", poems);
      }
    },
    updateHighlight({ state, commit, dispatch }, { category, value, action }) {
      const toResetHighlight = // When switching interacting categories, previous highlights are reset
        state.interactingCategory !== category ||
        // When interacting with theme related moods, only allow the one theme to be additive
        (state.interactingCategory ===
          INTERACTION_CATEGORIES.SANKEY_THEME_MOOD &&
          (state.highlightedThemes.length > 1 ||
            (state.highlightedThemes.length === 1 &&
              value[0] !== state.highlightedThemes[0]))) ||
        // When interacting with theme related motifs, only allow the one theme to be additive
        (state.interactingCategory ===
          INTERACTION_CATEGORIES.SANKEY_THEME_MOTIF &&
          (state.highlightedThemes.length > 1 ||
            (state.highlightedThemes.length === 1 &&
              value[0] !== state.highlightedThemes[0])));

      if (toResetHighlight) dispatch("resetHighlight");

      commit("updateInteractingCategory", category);

      // The interactingCategoryValues are used for poem panel filters details text
      // We only need to update this when click, not for hover highlight
      if (action === INTERACTION_ACTIONS.CLICK_TOGGLE) {
        let newValues;
        if (toResetHighlight) {
          newValues = [value];
        } else {
          const i = state.interactingCategoryValues
            .map(JSON.stringify)
            .indexOf(JSON.stringify(value));
          newValues = state.interactingCategoryValues.slice();
          if (i === -1) {
            newValues.push(value);
          } else {
            newValues.splice(i, 1);
          }
        }
        commit("updateInteractingCategoryValues", newValues);
      }

      // Wordcloud poem hover doesn't change sankey data, but click does
      const isHoveredThenClickedButNotWordcloudPoem =
        state.interactingAction === INTERACTION_ACTIONS.HOVER_ADD &&
        action === INTERACTION_ACTIONS.CLICK_TOGGLE &&
        category !== INTERACTION_CATEGORIES.WORDCLOUD_POEM;
      commit("updateInteractingAction", action);

      if (isHoveredThenClickedButNotWordcloudPoem) return;

      switch (category) {
        case INTERACTION_CATEGORIES.SANKEY_THEME:
          dispatch("updateHighlightedThemes", {
            value,
            action
          });
          break;
        case INTERACTION_CATEGORIES.SANKEY_MOOD:
          dispatch("updateHighlightedMoods", {
            value,
            action
          });
          break;
        case INTERACTION_CATEGORIES.SANKEY_THEME_MOOD:
          dispatch("updateHighlightedMoodsWithTheme", {
            value,
            action
          });
          break;
        case INTERACTION_CATEGORIES.SANKEY_MOTIF:
          dispatch("updateHighlightedMotifs", {
            value,
            action
          });
          break;
        case INTERACTION_CATEGORIES.SANKEY_THEME_MOTIF:
          dispatch("updateHighlightedMotifsWithTheme", {
            value,
            action
          });
          break;
        case INTERACTION_CATEGORIES.WORDCLOUD_POEM:
          dispatch("updateHighlightedByPoem", {
            value,
            action
          });
          break;
        default:
          break;
      }
    },
    resetHighlight({ commit }) {
      commit("updateHighlightFreezed", false);
      commit("updateHighlightedThemes", []);
      commit("updateHighlightedMoods", []);
      commit("updateHighlightedMotifs", []);
      commit("updateHighlightedThemeToMoodFlows", []);
      commit("updateHighlightedThemeToMotifFlows", []);
      commit("updateHighlightedPoems", []);
      commit("updateInteractingCategory", null);
      commit("updateInteractingCategoryValues", []);
    },
    updatePickedThemes({ dispatch, commit, state, getters }, theme) {
      let pickedThemes;
      if (theme === null) {
        // Reset tp default picked themes
        pickedThemes = getters.defaultPickedThemes;
      } else {
        pickedThemes = state.pickedThemes.slice();
        let foundIndex = pickedThemes.indexOf(theme);
        if (foundIndex === -1) {
          // theme word hasn't been added yet
          if (getters.additionalAllowedPickedThemesCount === 0) {
            // no space to add, remove the last one (the smallest count)
            pickedThemes.pop();
          }
          // add the new theme
          pickedThemes.push(theme);
          // order pickedThemes by count
          pickedThemes.sort((a, b) =>
            ascending(
              state.allThemes.findIndex(d => d.key === a),
              state.allThemes.findIndex(d => d.key === b)
            )
          );
        } else {
          // theme word has already been added
          pickedThemes.splice(foundIndex, 1);
        }
      }
      commit("updatePickedThemes", pickedThemes);
      const allMoodsOfPickedThemes = getAllTargetsOfPickedThemes(
        "mood",
        pickedThemes,
        state.filteredPoems
      );
      const allMotifsOfPickedThemes = getAllTargetsOfPickedThemes(
        "motif",
        pickedThemes,
        state.filteredPoems
      );
      commit("updateAllMoodsOfPickedThemes", allMoodsOfPickedThemes);
      commit("updateAllMotifsOfPickedThemes", allMotifsOfPickedThemes);
      commit("updatePickedMoods", []);
      commit("updatePickedMotifs", []);
      dispatch("updateSankey");
    },
    updatePickedPoem({ dispatch, commit, state }, poem) {
      const pickedPoem = state.pickedPoem === poem ? null : poem;
      commit("updatePickedPoem", pickedPoem);
      dispatch("updateSankey");
    },
    updatePickedMoods({ commit, dispatch }, moods) {
      commit("updatePickedMoods", moods);
      dispatch("updateSankey");
    },
    updatePickedMotifs({ commit, dispatch }, motifs) {
      commit("updatePickedMotifs", motifs);
      dispatch("updateSankey");
    },

    updateMotifThreshold({ commit, dispatch }, threshold) {
      commit("updateMotifThreshold", threshold);
      dispatch("updateSankey");
    },
    updateMoodThreshold({ commit, dispatch }, threshold) {
      commit("updateMoodThreshold", threshold);
      dispatch("updateSankey");
    },
    updateSankey({ commit, state, getters, dispatch }) {
      // Reset any Sankey highlights
      dispatch("resetHighlight");

      const poemsByTheme = new Map(
        poemsGroup.theme
          .all()
          .map(theme => [theme.key, Array.from(theme.value.items.values())])
      );

      const {
        poems: sankeyPoems,
        moodsSet: sankeyMoodsSet,
        themeToMoodFlows: sankeyThemeToMoodFlows,
        motifsSet: sankeyMotifsSet,
        themeToMotifFlows: sankeyThemeToMotifFlows
      } = getSankeyData(
        poemsByTheme,
        getters.sankeyThemesSet,
        state.pickedPoem,
        state.moodThreshold,
        state.motifThreshold,
        state.pickedMoods,
        state.pickedMotifs
      );

      commit("updateSankeyMoodsSet", sankeyMoodsSet);
      commit("updateSankeyMotifsSet", sankeyMotifsSet);
      commit("updateSankeyThemeToMoodFlows", sankeyThemeToMoodFlows);
      commit("updateSankeyThemeToMotifFlows", sankeyThemeToMotifFlows);
      commit("updateSankeyPoems", sankeyPoems);
    },
    updateFiltered({ commit, dispatch, getters }) {
      commit("updateHighlightFreezed", false);
      const filteredPoems = cf.allFiltered();
      commit("updateFilteredPoems", filteredPoems);
      const isFiltered = filteredPoems.length !== cf.size();
      commit("updateIsFiltered", isFiltered);
      const allThemes = getAllThemesSorted(poemsGroup.theme);
      commit("updateAllThemes", allThemes);
      commit("updatePickedPoem", null);
      const pickedThemes = getters.defaultPickedThemes;
      commit("updatePickedThemes", pickedThemes);
      const allMoodsOfPickedThemes = getAllTargetsOfPickedThemes(
        "mood",
        pickedThemes,
        filteredPoems
      );
      const allMotifsOfPickedThemes = getAllTargetsOfPickedThemes(
        "motif",
        pickedThemes,
        filteredPoems
      );
      commit("updateAllMoodsOfPickedThemes", allMoodsOfPickedThemes);
      commit("updateAllMotifsOfPickedThemes", allMotifsOfPickedThemes);
      commit("updatePickedMoods", []);
      commit("updatePickedMotifs", []);
      dispatch("updateSankey");
    },
    updateHighlightedYearsSentiments({ dispatch }, yearsSentiments) {
      const stringifiedYearsSentimentsSet = new Set(
        yearsSentiments.map(JSON.stringify)
      );
      poemsDimension.yearSentiment.filter(d =>
        stringifiedYearsSentimentsSet.has(JSON.stringify(d))
      );
      dispatch("updateFiltered");
    }
  },
  getters: {
    isHighlighting(state) {
      return (
        state.highlightedThemes.length +
          state.highlightedMotifs.length +
          state.highlightedMoods.length >
        0
      );
    },
    isThemeOpaque: (state, getters) => theme => {
      if (!getters.isHighlighting) return false;
      return !state.highlightedThemes.includes(theme);
    },
    sankeyThemesSet(state) {
      return new Set(state.pickedThemes);
    },
    defaultPickedThemes(state) {
      return state.allThemes.slice(0, 2).map(d => d.key);
    },
    additionalAllowedPickedThemesCount(state) {
      return MAX_PICKED_THEMES - state.pickedThemes.length;
    },
    colorScale(state, getters) {
      return scaleOrdinal()
        .domain([...getters.sankeyThemesSet])
        .range([
          "#7F3C8D",
          "#11A579",
          "#3969AC",
          "#F2B701",
          "#E73F74",
          "#80BA5A",
          "#E68310",
          "#008695",
          "#CF1C90",
          "#f97b72",
          "#4b4b8f",
          "#A5AA99"
        ])
        .unknown("#616161");
    },
    color: (state, getters) => theme => {
      return getters.colorScale(theme);
    },
    displayHighlightedNotFilteredPoems(state) {
      return state.highlightFreezed && state.highlightedPoems;
    },
    thresholdMax() {
      return 18;
    },
    isMotifThresholdDisabled(state) {
      return state.pickedMotifs.length > 0;
    },
    isMoodThresholdDisabled(state) {
      return state.pickedMoods.length > 0;
    },
    poemsTotal(state) {
      return state.poems.length;
    }
  }
});
